import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteCoupon, fetchCouponsList } from "../../shared/commerceMastersApi";
import { IconEye, IconPlus, IconUsages } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

export function CouponsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewCoupon, setViewCoupon] = useState(null);

  const rows = useMemo(
    () =>
      items.map((x) => ({
        id: x.id,
        coupon: x
      })),
    [items]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchCouponsList();
    if (!res.ok) {
      setError(res.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(res.items);
    }
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this coupon?"))) return;
      const res = await deleteCoupon(id);
      if (!res.ok) setError(res.error ?? "Failed to delete coupon");
      else await load();
    },
    [load]
  );

  const columns = useMemo(
    () => [
      {
        field: "code",
        headerName: "Code",
        flex: 0.7,
        minWidth: 100,
        valueGetter: (_v, row) => row.coupon.code ?? "-"
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1,
        minWidth: 120,
        valueGetter: (_v, row) => row.coupon.title ?? "-"
      },
      {
        field: "discpuntType",
        headerName: "Type",
        flex: 0.6,
        minWidth: 90,
        valueGetter: (_v, row) => row.coupon.discpuntType ?? "-"
      },
      {
        field: "discountValue",
        headerName: "Value",
        width: 90,
        valueGetter: (_v, row) => row.coupon.discountValue ?? 0
      },
      {
        field: "startDate",
        headerName: "Start Date",
        minWidth: 140,
        valueGetter: (_v, row) => formatDateTime(row.coupon.startDate)
      },
      {
        field: "endDate",
        headerName: "End Date",
        minWidth: 140,
        valueGetter: (_v, row) => formatDateTime(row.coupon.endDate)
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 190,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const x = params.row.coupon;
          return (
            <div className="table-actions">
              <button
                className="icon-button"
                type="button"
                title="View"
                onClick={async () => {
                  const res = await apiGet(`/api/Coupons/${x.id}`);
                  if (!res.ok) setError(res.error ?? "Failed to load coupon");
                  else setViewCoupon(res.data);
                }}
              >
                <IconEye />
              </button>
              <button className="icon-button" type="button" title="Edit" onClick={() => navigate(`/coupons?edit=${x.id}`)}>
                ✏️
              </button>
              <button
                className="icon-button"
                type="button"
                title="Coupon usages"
                aria-label="Coupon usages"
                onClick={() => navigate(`/coupon-usages?coupon=${x.id}`)}
              >
                <IconUsages />
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(x.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [navigate, handleDeleteClick]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Coupons</h2>
          <p>{loading ? "Loading coupons..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/coupons" className="icon-button icon-button--toolbar" title="Add coupon" aria-label="Add coupon">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={{
              border: "none",
              fontFamily: "inherit",
              fontSize: "0.9rem",
              height: "100%",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f2fcf5",
                borderBottom: "1px solid #e8ecea",
                fontWeight: 600
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e8ecea",
                backgroundColor: "#fbfffc"
              },
              "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
                backgroundColor: "#fcfffd"
              },
              "& .MuiDataGrid-cell": {
                borderBottomColor: "#e8ecea"
              }
            }}
          />
        </div>
      </div>
      <RecordViewDrawer
        open={Boolean(viewCoupon)}
        onClose={() => setViewCoupon(null)}
        label="Coupon details"
        title={viewCoupon?.title ?? "—"}
        subtitle={viewCoupon?.code}
        ariaLabel="Coupon details"
        sections={
          viewCoupon
            ? [
                {
                  title: "Coupon Details",
                  columns: 2,
                  fields: [
                    ["Title", viewCoupon.title ?? "-"],
                    ["Code", viewCoupon.code ?? "-"],
                    ["Type", viewCoupon.discpuntType ?? "-"],
                    ["Value", viewCoupon.discountValue ?? 0],
                    ["Min Order Amount", viewCoupon.minOrderAmount ?? 0],
                    ["Max Discount Amount", viewCoupon.maxDiscountAmount ?? 0],
                    ["Usage Limit", viewCoupon.usageLimit ?? 0],
                    ["Customer Usage Limit", viewCoupon.customerUsageLimit ?? 0],
                    ["Applicable Type", viewCoupon.applicableType ?? "-"],
                    ["Applicable Id", viewCoupon.applicableId ?? "-"],
                    ["Start Date", formatDateTime(viewCoupon.startDate)],
                    ["End Date", formatDateTime(viewCoupon.endDate)],
                    ["Created By", viewCoupon.createdById ?? "-"],
                    ["Created Date", formatDateTime(viewCoupon.createdDate)],
                    ["Updated Date", formatDateTime(viewCoupon.updatedDate)]
                  ]
                },
                {
                  title: "Description",
                  fields: [["Description", viewCoupon.description ?? "-", true]]
                }
              ]
            : []
        }
      />
    </div>
  );
}
